import React, { useState } from "react";
import { Box, Tab, Tabs, Typography } from "@mui/material";
import { styled } from "@mui/system";
import {
  ElementIcon,
  ElementLocatorIcon,
  EqualizerIcon,
  ScriptIcon,
} from "../../components-new/icons";

import { TestDataConfig, ElementLocator, Script, Elements } from "./Edit";
import ApiEndpoints from "./Edit/ApiEndpoints";
import Conditions from "./Edit/Conditions";
import { useSelector } from "react-redux";
import HelpOutlineRoundedIcon from "@mui/icons-material/HelpOutlineRounded";
import CloseOutlinedIcon from "@mui/icons-material/CloseOutlined";
import { Api } from "@mui/icons-material";

const StyledTabs = styled(Tabs)(({ theme }) => ({
  backgroundColor: "transparent",
  width: "auto",
  "& .MuiTabs-flexContainer": {
    columnGap: "2px",
  },
  "& .MuiTabs-indicator": {
    background: `#ffffff`,
  },
}));

const StyledTab = styled(Tab)(({ theme }) => ({
  background: "#ffffff",
  borderBottom: "1px solid #E2E6F3",
  "& p": {
    color: "#404040",
  },
  "&.Mui-selected": {
    border: 0,
    background: `#2550D1`,
    "& div": {
      color: "#ffffff",
    },
    "& svg, & p": {
      color: "#ffffff",
    },
  },
  "&:hover": {
    backgroundColor: "#2550D1",
    "& svg": {
      color: "#ffffff",
    },
    "& p": {
      color: "#ffffff",
    },
  },
}));

const CustomTab = styled(Box)(({ theme }) => ({
  display: "flex",
  columnGap: "7px",
  textTransform: "capitalize",
  width: "100%",
  alignItems: "center",
  "& svg": {
    width: "23px",
    color: "#8C9EB8",
  },
}));

function CustomTabPanel(props) {
  const { children, value, index, ...other } = props;

  return (
    <Box
      role="tabpanel"
      hidden={value !== index}
      id={`simple-tabpanel-${index}`}
      aria-labelledby={`simple-tab-${index}`}
      {...other}
    >
      {value === index && <>{children}</>}
    </Box>
  );
}

const EditTabContainer = ({ item }) => {
  const [value, setValue] = useState(0);
  const editStep = useSelector((state) => state.authoringSlice.editStep);
  const [showHelp, setShowHelp] = useState(false);
  const tabs = useSelector((state) => state.authoringSlice.tabs) || [];

  const handleCloseHelp = () => {
    setShowHelp(false);
  };

  return (
    <Box
      display="flex"
      bgcolor="#ffffff"
      py={4}
      columnGap={"20px"}
      boxShadow={`0px 24px 23.3px 0px rgba(0, 0, 0, 0.03)`}
      borderRadius={`0px 0px 12px 12px`}
      position={"relative"}
    >
      <Box
        backgroundColor="#cccccc"
        width="44px"
        height="44px"
        display="flex"
        alignItems="center"
        justifyContent="center"
        borderRadius="50px"
        sx={{ cursor: "pointer" }}
        onClick={() => setShowHelp(true)}
        position="absolute"
        bottom={"20px"}
        right={"20px"}
      >
        <HelpOutlineRoundedIcon color="#ffffff" />
      </Box>
      <Box>
        <StyledTabs
          orientation="vertical"
          value={value}
          onChange={(event, newValue) => setValue(newValue)}
        >
          <StyledTab
            label={
              <CustomTab>
                <EqualizerIcon />
                <Typography fontSize={"14px"} fontWeight={500}>
                  Test Data Configuration
                </Typography>
              </CustomTab>
            }
          />
          <StyledTab
            disabled={item.Step_Number === 0}
            label={
              <CustomTab>
                <ElementIcon />
                <Typography fontSize={"14px"} fontWeight={500}>
                  Element Details
                </Typography>
              </CustomTab>
            }
          />
          <StyledTab
            disabled={item.Step_Number === 0}
            label={
              <CustomTab>
                <ElementLocatorIcon />
                <Typography fontSize={"14px"} fontWeight={500}>
                  Element Locator
                </Typography>
              </CustomTab>
            }
          />
          <StyledTab
            disabled={item.Step_Number === 0}
            label={
              <CustomTab>
                <ScriptIcon />
                <Typography fontSize={"14px"} fontWeight={500}>
                  Scripts
                </Typography>
              </CustomTab>
            }
          />
          <StyledTab
            disabled={item.Step_Number === 0}
            label={
              <CustomTab>
                <Api />
                <Typography fontSize={"14px"} fontWeight={500}>
                  Configure Invoked API's
                </Typography>
              </CustomTab>
            }
          />
          <StyledTab
            disabled={item.Step_Number === 0}
            label={
              <CustomTab>
                <ScriptIcon />
                <Typography fontSize={"14px"} fontWeight={500}>
                  Conditional Logic
                </Typography>
              </CustomTab>
            }
          />
        </StyledTabs>
      </Box>

      <Box flex={2} pb={"30px"} overflow="scroll">
        <CustomTabPanel value={value} index={0}>
          <TestDataConfig item={editStep} />
        </CustomTabPanel>
        <CustomTabPanel value={value} index={1}>
          <Elements item={editStep} />
        </CustomTabPanel>
        <CustomTabPanel value={value} index={2}>
          <ElementLocator item={editStep} tabs={tabs} />
        </CustomTabPanel>
        <CustomTabPanel value={value} index={3}>
          <Script item={item} />
        </CustomTabPanel>
        <CustomTabPanel value={value} index={4}>
          <ApiEndpoints item={editStep} />
        </CustomTabPanel>
        <CustomTabPanel value={value} index={5}>
          <Conditions item={editStep} />
        </CustomTabPanel>
      </Box>
      {showHelp && (
        <Box flex={1} maxWidth={"250px"} p={3} borderLeft={`2px solid #E2E6F3`}>
          <Box mb={2} display="flex" justifyContent="right">
            <Box
              backgroundColor="#cccccc"
              width="44px"
              height="44px"
              display="flex"
              alignItems="center"
              justifyContent="center"
              borderRadius="50px"
              sx={{ cursor: "pointer" }}
              onClick={handleCloseHelp}
            >
              <CloseOutlinedIcon color="#ffffff" />
            </Box>
          </Box>
          <Box>
            <Typography fontWeight="bold">Keyword</Typography>
            <Typography>
              Keyword is a html element on which you have performed some action, you can select an
              action only after selecting an element.
            </Typography>
          </Box>
        </Box>
      )}
    </Box>
  );
};

export default EditTabContainer;
